/* src/mock/index.js — dev-only offline backend for the demo run.
 * NOT part of the recovered app. Enabled only when VITE_MOCK=1 (see main.js).
 *
 * How it works: both call-sites (src/api/request.js and src/realtime/api.js) import the
 * DEFAULT axios instance, so overriding `axios.defaults.adapter` intercepts every POST
 * without touching any of the verbatim source contracts. We match on the Navixy action
 * string (the path after the api host) and return a Navixy-v2-shaped envelope.
 *
 * We also flip the realtime engine to poller mode (websockets:false) so it never tries a
 * real wss://api.lacak.io socket — the tracker/get_states poller then drives live tiles
 * straight from this mock.
 */

import axios from 'axios';
import { setConfig as setRealtimeConfig } from '../realtime/config.js';
import { USER_INFO, TRACKERS, buildStates, BLOCKED, MAP_LAYERS, GROUPS, PLACES, setOutput, buildTrack, buildEvents } from './data.js';

const DUMMY_HASH = 'DEMO-SESSION-KEY-0123456789abcdef';
const CHAT = {}; // per-tracker chat message store (demo)
const GEOLINKS = []; // "Share location" geo-links created this session (demo)
let GEOLINK_SEQ = 4200; // geo-link id sequence (demo)
// demo geofences/zones for the map "geofences" tool (circle + polygon around Jakarta)
const GEOFENCES = [
  { id: 1, label: 'Jakarta CBD', type: 'circle', color: '#8e44ad', center: { lat: -6.2088, lng: 106.8456 }, radius: 2600 },
  { id: 2, label: 'Depot North', type: 'circle', color: '#16a085', center: { lat: -6.1600, lng: 106.8300 }, radius: 1800 },
  // a corridor/sausage zone: a route centreline + a width (buffered on the map).
  { id: 3, label: 'Sudirman corridor', type: 'corridor', color: '#e67e22', radius: 220, path: [
    { lat: -6.1950, lng: 106.8225 }, { lat: -6.2080, lng: 106.8210 },
    { lat: -6.2230, lng: 106.8260 }, { lat: -6.2360, lng: 106.8320 }
  ] }
];
// per-tracker unread chat counts (demo): seed a few so the row badge is visible.
const CHAT_UNREAD = { 102: 3, 105: 1, 110: 12, 118: 105 };

// Device-settings Tier-2: a server-authored JSON Schema (tracker/settings/tracking/schema/read).
// The apiMethod lives INSIDE the schema, so the SchemaForm engine saves to it with zero
// frontend code. Shape mirrors the real VJsf schema (object → object → typed leaves).
const DEVICE_TRACKING_SCHEMA = {
  type: 'object',
  title: 'Tracking settings',
  apiMethod: 'tracker/settings/tracking/update',
  properties: {
    general: {
      type: 'object', title: 'General',
      properties: {
        device_name: { type: 'string', title: 'Device name' },
        timezone: { type: 'string', title: 'Time zone', enum: ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura', 'UTC'] },
        sim_phone: { type: 'string', title: 'SIM phone number' }
      }
    },
    tracking: {
      type: 'object', title: 'Tracking',
      properties: {
        interval_seconds: { type: 'integer', title: 'Report interval (seconds)', minimum: 10 },
        distance_meters: { type: 'integer', title: 'Report by distance (m)' },
        enable_lbs: { type: 'boolean', title: 'Fall back to LBS when GPS is lost' }
      }
    },
    sensors: {
      type: 'object', title: 'Sensors',
      properties: {
        ignition_input: { type: 'string', title: 'Ignition input', enum: ['in1', 'in2', 'in3', 'none'] },
        fuel_calibrated: { type: 'boolean', title: 'Fuel sensor calibrated' }
      }
    }
  }
};
const DEVICE_TRACKING_VALUE = {
  general: { device_name: 'Unit', timezone: 'Asia/Jakarta', sim_phone: '' },
  tracking: { interval_seconds: 30, distance_meters: 100, enable_lbs: true },
  sensors: { ignition_input: 'in1', fuel_calibrated: false }
};

// Report generate/poll bookkeeping (demo).
let REPORT_SEQ = 9000;
const REPORT_PROGRESS = {}; // report_id → percent_ready
const REPORT_TRACKER = {};  // report_id → tracker_id it was generated for
// A demo interactive report document (retrieve shape: {title, sheets:[{header, sections:[{type,...}]}]}).
function buildReport(trackerId) {
  const t = TRACKERS.find((x) => String(x.id) === String(trackerId));
  const name = (t && t.label) || ('#' + (trackerId != null ? trackerId : '?'));
  return {
    title: 'Trip report — ' + name,
    sheets: [
      { header: 'Summary', sections: [
        { type: 'text', text: 'Report for ' + name + ': 4 trips, total distance 92.6 km, driving time 3h 12m, max speed 82 km/h, 1 stop over 30 min.' },
        { type: 'table', header: 'Trips', columns: [
          { title: 'Trip', field: 'trip' },
          { title: 'Start', field: 'start' },
          { title: 'End', field: 'end' },
          { title: 'Distance (km)', field: 'dist', highlight_min_max: true },
          { title: 'Max speed', field: 'maxspd', highlight_min_max: true }
        ], data: [{ rows: [
          { trip: 1, start: '08:12', end: '09:03', dist: 24.6, maxspd: 82 },
          { trip: 2, start: '10:20', end: '10:58', dist: 18.2, maxspd: 66 },
          { trip: 3, start: '13:05', end: '14:10', dist: 31.4, maxspd: 79 },
          { trip: 4, start: '16:30', end: '17:02', dist: 18.4, maxspd: 71 }
        ] }] }
      ] }
    ]
  };
}

// Pull the bare action string out of whatever URL the engines built
// ("//api.lacak.io/user/auth", "https://api.lacak.io/tracker/list", "/api/…").
function endpointOf(url) {
  if (!url) return '';
  let u = String(url);
  const host = u.indexOf('api.lacak.io/');
  if (host !== -1) return u.slice(host + 'api.lacak.io/'.length);
  return u.replace(/^https?:\/\/[^/]+\//, '').replace(/^\/+(api\/)?/, '');
}

function parseBody(data) {
  if (!data) return {};
  if (typeof data === 'string') { try { return JSON.parse(data); } catch (e) { return {}; } }
  return data; // FormData etc. — not needed by the mocked endpoints
}

// action -> (params) -> response payload (the object that becomes response.data)
const ROUTES = {
  'user/auth': () => ({ success: true, type: 'authenticated', hash: DUMMY_HASH }),

  'user/get_info': () => Object.assign({ success: true }, USER_INFO),

  'user/logout': () => ({ success: true }),
  'user/audit/checkin': () => ({ success: true }),

  // per-tracker chat (polled). Seed a couple of incoming messages per tracker.
  'tracker/chat/message/list': (p) => {
    const id = (p && p.tracker_id) || 0
    if (!CHAT[id]) CHAT[id] = [
      { text: 'Unit on the way to destination.', incoming: true, time: '09:14' },
      { text: 'ETA 25 minutes.', incoming: true, time: '09:15' }
    ]
    delete CHAT_UNREAD[id] // opening the conversation marks it read
    // Return a COPY so a consumer's optimistic push can't mutate the mock store.
    return { success: true, list: CHAT[id].slice() }
  },

  // per-tracker unread chat counts (drives the row chat badge)
  'tracker/chat/unread/list': () => ({ success: true, value: Object.assign({}, CHAT_UNREAD) }),
  'tracker/chat/message/create': (p) => {
    const id = (p && p.tracker_id) || 0
    if (!CHAT[id]) CHAT[id] = []
    const text = p && p.text
    if (text) {
      // 24h HH:MM to match the seeded message timestamps (one clock format per conversation)
      CHAT[id].push({ text, incoming: false, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) })
    }
    return { success: true }
  },

  // "Share location" → create a public geo-link for a tracker. Returns a shareable URL.
  'geolink/tracker/create': (p) => {
    const id = GEOLINK_SEQ++
    const token = id.toString(36) + Math.random().toString(36).slice(2, 10)
    const url = 'https://go.lacak.io/#/geo/' + token
    const link = { id, tracker_id: (p && p.tracker_id) || 0, description: (p && p.description) || '', expires: (p && p.expires) || null, url }
    GEOLINKS.push(link)
    return { success: true, id, url, value: link }
  },
  'geolink/tracker/list': () => ({ success: true, list: GEOLINKS.slice() }),
  'geolink/tracker/delete': (p) => {
    const i = GEOLINKS.findIndex((g) => g.id === (p && p.id))
    if (i !== -1) GEOLINKS.splice(i, 1)
    return { success: true }
  },

  'tracker/list': () => ({ success: true, list: TRACKERS }),

  'tracker/group/list': () => ({ success: true, list: GROUPS }),

  'map_layer/list': () => ({ success: true, list: MAP_LAYERS }),

  // saved places / POIs (rendered on the map as place pins + radius circles)
  'place/list': () => ({ success: true, list: PLACES }),

  // device-settings Tier-2 schema portlet: server-authored JSON Schema + current values.
  'tracker/settings/tracking/schema/read': () => ({ success: true, schema: DEVICE_TRACKING_SCHEMA, value: DEVICE_TRACKING_VALUE }),
  'tracker/settings/tracking/update': () => ({ success: true }),

  // Reports: generate → poll status (@200ms, +40%/poll → done in ~3 polls) → retrieve doc.
  'report/tracker/generate': (p) => { const id = REPORT_SEQ++; REPORT_PROGRESS[id] = 0; REPORT_TRACKER[id] = (p && p.tracker_id) != null ? p.tracker_id : null; return { success: true, id }; },
  'report/tracker/status': (p) => { const id = p && p.report_id; const pct = Math.min(100, (REPORT_PROGRESS[id] || 0) + 40); REPORT_PROGRESS[id] = pct; return { success: true, percent_ready: pct }; },
  'report/tracker/retrieve': (p) => ({ success: true, report: buildReport(REPORT_TRACKER[p && p.report_id] != null ? REPORT_TRACKER[p.report_id] : (p && p.report_id)) }),

  // geofences/zones (the map toolbar "geofences" tool) — a couple of demo zones.
  'zone/list': () => ({ success: true, list: GEOFENCES }),

  // outputs widget write path (turn a relay/engine-block output on/off).
  // Real param shape is {tracker_id, output, enable}; also accept the older {value}.
  'tracker/output/set': (p) => setOutput(p && p.tracker_id, p && (p.enable !== undefined ? p.enable : p.value)),

  // historical track for the history-playback bar (period_hours = window size; from/to anchor
  // the point times to the requested window so different date windows don't collide in cache)
  'track/read': (p) => ({ success: true, list: buildTrack((p && p.tracker_id) || 100, p && p.period_hours, p && p.from, p && p.to) }),
  'track/list': (p) => ({ success: true, list: [{ id: 1, tracker_id: (p && p.tracker_id) || 100, points: buildTrack((p && p.tracker_id) || 100, p && p.period_hours, p && p.from, p && p.to).length }] }),

  // history events feed (the travel-history EVENTS tab). Real action string + `list` root key;
  // params mirror the deployed fetch ({ tracker_id, from, to, ascending, limit, events }).
  'history/tracker/list': (p) => ({ success: true, list: buildEvents((p && p.tracker_id) || 100, p && p.period_hours, p && p.from, p && p.to) }),

  'tracker/get_states': (params) => {
    const wanted = (params && params.trackers) || TRACKERS.map((t) => t.id);
    const all = buildStates();
    const states = {};
    wanted.forEach((id) => { if (all[id]) states[id] = all[id]; });
    return {
      success: true,
      user_time: new Date().toISOString(),
      states,
      blocked: BLOCKED,
      not_exist: {}
    };
  }
};

function respond(config, payload) {
  return Promise.resolve({
    data: payload,
    status: 200,
    statusText: 'OK',
    headers: { 'content-type': 'application/json' },
    config,
    request: {}
  });
}

export function installMock() {
  // Force the realtime engine onto the HTTP poller (no real websocket).
  setRealtimeConfig({ websockets: false });

  axios.defaults.adapter = function mockAdapter(config) {
    const action = endpointOf(config.url);
    const params = parseBody(config.data);
    const handler = ROUTES[action];

    // eslint-disable-next-line no-console
    console.log('[mock] ' + action, params && Object.keys(params).length ? params : '');

    if (handler) return respond(config, handler(params));

    // Unmocked action: return a benign success envelope so nothing hard-fails.
    return respond(config, { success: true });
  };

  // eslint-disable-next-line no-console
  console.log('[mock] offline backend installed — ' + TRACKERS.length + ' dummy trackers');
}

export default installMock;
